<template>
  <div class="StudyDetails" v-if="selectedStudy && currentTab">
    <v-card class="card-create-step" outlined>
			<v-alert
				class="edit-alert"
				v-model="saveSuccess"
				dismissible
				type="success"
				absolute
			>
				Changes have been saved.
			</v-alert>
			<v-alert
				class="edit-alert"
				v-model="hasErrors"
				dismissible
				type="error"
				absolute
			>
				Changes not saved. One or more fields have errors.
			</v-alert>
      <!-- <v-card-title>Specify Study Details</v-card-title> -->
			<h2 class="left-align">Specify Study Details</h2>
									<p>All sections on this page are required</p>
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-row>
          <v-col md="6">
            <h3 class="left-align">Study tool:</h3>
            <v-btn-toggle
              row
              id="form-study-tool"
            >
              <v-btn
								v-if="studyToSave.studyTool === this.$createStudyLists.STUDY_TOOL_RECALL.value"
                class="btn-tool-toggle text-none no-click"
                rounded
              >
							<span style="color:white">
                {{ this.$createStudyLists.studyTools[0].name }}
                <br />
                {{ this.$createStudyLists.studyTools[0].description }}
								</span>
              </v-btn>

							<v-btn
								v-else-if="studyToSave.studyTool === this.$createStudyLists.STUDY_TOOL_RECORD.value"
                class="btn-tool-toggle text-none no-click"
                rounded
              >
							<span style="color:white">
                {{ this.$createStudyLists.studyTools[1].name }}
                <br />
                {{ this.$createStudyLists.studyTools[1].description }}
								</span>
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col md="6" cols="12">
            <explanation-box
							:color="'red'"
							:displayText="'A <b>recall</b> asks respondents to report all foods and drinks consumed the previous day from midnight to midnight or during the previous 24 hours prior to the time of login.<br><br>A <b>record</b> (also known as a food diary) asks respondents to report all foods and drinks consumed in real time.<br><br><em> To learn more about 24-hour recalls and food records, visit NCI’s <a href=&quot;https://dietassessmentprimer.cancer.gov/&quot; target=&quot;_blank&quot;>Dietary Assessment Primer</a>. The Primer can help researchers choose the best dietary assessment approach for their research objectives. </em>'">
						</explanation-box>
          </v-col>
        </v-row>
        <v-divider inset></v-divider>
        <!-- Recall specific inputs -->
        <div
          class="tool-specific"
          v-if="studyToSave.studyTool === this.$createStudyLists.STUDY_TOOL_RECALL.value"
        >
          <v-card-title class="study-tool-title"
            >{{
              this.$createStudyLists.studyTools[0].name
            }}
            Settings</v-card-title
          >
          <v-row>
						<v-col md="2">
							<h3 class="left-align">Study type: (recall)</h3>
						</v-col>
            <v-col md="4">
							<fieldset class="border-0">
              <v-radio-group
                v-model="studyToSave.recallType"
                :rules="radioRules"
                id="form-study-type-1"
								:disabled="selectedStudy.countRespondents !== 0"
              >
                <v-radio
                  v-for="(type, i) in this.$createStudyLists.recallStudyTypes"
                  :key="type.key + i"
                  :label="type.key"
                  :value="type.value"
				:aria-label="'Study type: (recall) ' + type.value + ' Description: ' + studyTypeText"
                ></v-radio>
              </v-radio-group>
							</fieldset>
            </v-col>
            <v-col md="6" cols="12">
              <explanation-box
							id="form-study-type-1-explanation-box2"
								:color="'yellow'"
								:displayText="studyTypeText">
							</explanation-box>
            </v-col>
          </v-row>
          <v-divider inset></v-divider>
          <v-row>
						<v-col md="2">
							<h3 class="left-align">Total number of recalls per respondents:</h3>
						</v-col>
            <v-col md="4">
              <v-text-field
								ref="firstFocusableRecall"
                v-model="studyToSave.numRecallsPerParticipant"
                hide-details="auto"
                outlined
                :rules="requiredFieldRule"
                type="number"
				min="0"
                id="form-total-recalls-respondent"
				name="form-total-recalls-respondent"
				:aria-label="'Total number of recalls per respondents: ' + studyToSave.numRecallsPerParticipant + ' Description: ' + numOfRecallsPerRespondentsText"
              >
              </v-text-field>
            </v-col>
            <v-col md="6" cols="12">
              <explanation-box
				id="form-total-recalls-respondent-explanation-box2"
				:color="'green'"
				:displayText="numOfRecallsPerRespondentsText">
				</explanation-box>
            </v-col>
          </v-row>
          <v-divider inset></v-divider>
          <v-row>
						<v-col md="2">
							<h3 class="left-align">Intake time frame:</h3>
						</v-col>
            <v-col md="4">
							<fieldset class="border-0">
              <v-radio-group
                v-model="intakeTimeFrame"
                :rules="radioRules"
                id="form-intake-time"
              >
                <v-radio
                  v-for="(timeFrame, i) in this.$createStudyLists.intakeTimeFrames"
                  :key="timeFrame.key + i"
                  :label="timeFrame.key"
                  :value="timeFrame.key"
				  :aria-label="'Intake time frame: ' + timeFrame.key + ' Description: ' + intakeTimeFrameText"
                ></v-radio>
              </v-radio-group>
							</fieldset>
            </v-col>
            <v-col md="6" cols="12">
              <explanation-box

								:color="'green'"
								:displayText="intakeTimeFrameText">
							</explanation-box>
            </v-col>
          </v-row>
          <v-divider inset></v-divider>
          <v-row>
						<v-col md="2">
							<h3 class="left-align">Number of logins allowed:</h3>
						</v-col>
            <v-col md="4">
							<fieldset class="border-0">
              <v-radio-group
                v-model="numLoginsAllowed"
                :rules="radioRules"
                id="form-num-logins"
              >
                <v-radio
                  v-for="(loginAllowedItem, i) in this.$createStudyLists.loginsAllowedList"
                  :key="loginAllowedItem + i"
                  :label="loginAllowedItem"
                  :value="loginAllowedItem"
									:aria-label="'Number of logins allowed: ' + loginAllowedItem + ' Description: ' + numOfLoginsAllowedText "
                ></v-radio>
              </v-radio-group>
							</fieldset>
            </v-col>
            <v-col md="6" cols="12">
              <explanation-box
								id="form-num-logins-explanation-box2"
								:color="'green'"
								:displayText="numOfLoginsAllowedText">
							</explanation-box>
            </v-col>
          </v-row>
          <v-divider inset></v-divider>
          <v-row>
						<v-col md="2">
							<h3 class="left-align">Time provided to complete recall:</h3>
						</v-col>
            <v-col md="4">
							<fieldset class="border-0">
								<legend class="d-sr-only">
									Time Provided to Complete Recall:
								</legend>
              <p v-if="studyToSave.numberHoursToComplete === '24'" class="p2 left-align">{{this.$createStudyLists.timeToCompleteList[0]}}</p>
							<p v-else class="p2 left-align">{{this.$createStudyLists.timeToCompleteList[1]}}</p>
							</fieldset>
            </v-col>
            <v-col md="6" cols="12">
              <explanation-box
								:color="'red'"
								:displayText="'<strong>By midnight:</strong> For example, if a respondent is to report what they consumed Saturday, June 1, selecting &quot;By midnight&quot; allows the respondent to login any time between 12:01 am to 11:59 pm Sunday, June 2, to report intake for June 1. This option gives 24-hours for recalls.<br><br><strong>By 8 am:</strong> Allows the Respondent to login any time between 12:01 am Sunday, June 2, until 8:00 am Monday, June 3 to report intake for June 1. This option gives 36-hours for recalls.<br><br>All times are relative to a respondent’s time zone.'">
							</explanation-box>
            </v-col>
          </v-row>
        </div>
        <!-- Record specific inputs -->
        <div
          class="tool-specific"
          v-else-if="studyToSave.studyTool === this.$createStudyLists.STUDY_TOOL_RECORD.value"
        >
          <v-card-title class="study-tool-title">
						{{this.$createStudyLists.studyTools[1].name}}
            Settings
					</v-card-title>
          <v-row>
						<v-col md="2">
							<h3 class="left-align">Study type:</h3>
						</v-col>
            <v-col md="4">
							<fieldset class="border-0">
								<legend class="d-sr-only">
									Study type:
								</legend>
							<v-radio-group v-model="studyToSave.recordStudyType"
															:rules="radioRules"
															disabled="true"
															id="form-study-type-2">
								<v-radio v-for="(type, i) in this.$createStudyLists.recordStudyTypes"
													:key="type.value + i"
													:label="type.key"
													:value="type.value"
													aria-describedby="form-study-type-2-explanation-box2"></v-radio>
							</v-radio-group>
							</fieldset>
            </v-col>
            <v-col md="6" cols="12">
              <explanation-box
							id="form-study-type-2-explanation-box2"
								:color="'red'"
								:displayText="'<strong>Consecutive-day:</strong> Respondents complete food records for two or more consecutive days. A consecutive-day record will begin on the first day a respondent logs in and the subsequent two days.<br><br><strong>Single-day:</strong> Each respondent completes one or more records, each for one intake day. In the case of multiple single-day records, the intake days must be separated by at least one day. For consecutive intake days, choose a multiple-day record.'">
							</explanation-box>
            </v-col>
          </v-row>
          <v-divider inset></v-divider>
          <div v-if="studyToSave.recordStudyType === this.$createStudyLists.RECORD_TYPE_CONSECUTIVE.value">
            <v-row>
							<v-col md="2">
								<h3 class="left-align"><label for="form-num-consecutive-days">Number of consecutive days:</label></h3>
							</v-col>
              <v-col md="4">
                <v-text-field
									ref="firstFocusableRecord"
                  v-model="studyToSave.numConsecutiveRecordDays"
                  hide-details="auto"
                  outlined
                  :rules="requiredFieldRule"
                  type="number"
									min="0"
                  id="form-num-consecutive-days"
									name="form-num-consecutive-days"
									aria-describedby="form-num-consecutive-days-explanation-box2"
                >
                </v-text-field>
              </v-col>
              <v-col md="6" cols="12">
                <explanation-box
								id="form-num-consecutive-days-explanation-box2"
									:color="'green'"
									:fullBox=true
									:displayText="'The number of consecutive intake days you want to collect within each record. For example, 3 consecutive intake days in one record. Multiple consecutive-day records must have the same number of consecutive intake days within each record.'">
								</explanation-box>
              </v-col>
            </v-row>
            <v-divider inset></v-divider>
          </div>
          <v-row>
						<v-col md="2">
							<h3 class="left-align">Number of records:</h3>
						</v-col>
            <v-col md="4">
              <v-text-field
								ref="firstFocusableRecordSingle"
                v-model="studyToSave.numRecordsPerParticipant"
                hide-details="auto"
                outlined
                :rules="requiredFieldRule"
                type="number"
								min="0"
                id="form-num-records"
								name="form-num-records"
								:aria-label="'Number of records: ' + studyToSave.numRecordsPerParticipant + ' Description: ' + numOfRecordsText "
              >
              </v-text-field>
            </v-col>
            <v-col md="6" cols="12">
              <explanation-box
							id="form-num-records-explanation-box2"
								:color="'green'"
								:displayText="numOfRecordsText">
							</explanation-box>
            </v-col>
          </v-row>
        </div>
        <!-- Common questions for both study tools -->
        <v-row>
					<v-col md="2">
						<h3 class="left-align"><label for="form-version">ASA24 version:</label></h3>
					</v-col>
          <v-col md="4">
            <v-select
              :items="this.getAllStudyVersions"
							item-text="versionName"
							item-value="versionId"
              outlined
              v-model="version"
              :rules="radioRules"
              id="form-version"
							disabled
							aria-describedby="form-version-explanation-box2"
            ></v-select>
          </v-col>
          <v-col md="6" cols="12">
            <explanation-box
						id="form-version-explanation-box2"
							:fullBox=true
							:color="'red'"
							:displayText="'Select the respondent version.<br><br>Note: there are specialized versions for Canada and Australia. These versions have been modified to reflect the Canadian and Australian food supplies, respectively.'">
						</explanation-box>
          </v-col>
        </v-row>
        <v-divider inset></v-divider>
        <v-row>
					<v-col md="2">
						<h3 class="left-align">Number of Respondents:</h3>
					</v-col>
          <v-col md="4">
            <v-text-field
              v-model="studyToSave.numParticipants"
              hide-details="auto"
              outlined
              :rules="requiredFieldRule"
              type="number"
							min="0"
              id="form-respondent-num"
							name="form-respondent-num"
							:aria-label="'Number of Respondents: ' + studyToSave.numParticipants + ' Description: ' + numOfRespondentsText "
            >
            </v-text-field>
          </v-col>
          <v-col md="6" cols="12">
            <explanation-box
						id="form-respondent-num-explanation-box2"
							:color="'green'"
							:displayText="numOfRespondentsText">
						</explanation-box>
          </v-col>
        </v-row>
        <v-divider inset></v-divider>
        <v-row>
					<v-col md="2">
						<h3 class="left-align">Study start date:</h3>
					</v-col>
          <v-col md="4">
						<v-menu  v-model="startDateMenu" :close-on-content-click="false" offset-x tile allow-overflow >
  							<template v-slot:activator="{ on, attrs }">
									<v-text-field
										v-model="startDate"
              			hide-details="auto"
              			outlined
              			:rules="startDateRules"
              			type="date"
              			id="form-start-date"
										name="form-start-date"
										v-bind="attrs"
      							v-on="on"
										@keydown.tab='startDateMenu = false'
										:aria-label="'Study start date: ' + startDate + ' Description: ' + startDateText "								>
									</v-text-field>
								</template>
								<v-date-picker v-model="startDate" color="#42729B" style="margin: 0px; float: left" @input="startDateMenu = false"></v-date-picker>
							</v-menu>
          </v-col>
          <v-col md="6" cols="12">
            <explanation-box
							id="form-start-date-explanation-box2"
							:color="'green'"
							:displayText=startDateText>
						</explanation-box>
          </v-col>
        </v-row>
        <v-divider inset></v-divider>
        <v-row>
					<v-col md="2">
						<h3 class="left-align">Study end date:</h3>
					</v-col>
          <v-col md="4">
						<v-menu  v-model="endDateMenu" :close-on-content-click="false" offset-x tile allow-overflow>
  						<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="endDate"
              		hide-details="auto"
              		outlined
              		:rules="endDateRules"
              		type="date"
              		id="form-end-date"
									name="form-end-date"
									v-bind="attrs"
      						v-on="on"
									@keydown.tab='endDateMenu = false'
									:aria-label="'Study end date: ' + endDate + ' Description: ' + endDateText "
								>
								</v-text-field>
							</template>
								<v-date-picker v-model="endDate" color="#42729B" style="margin: 0px; float: left" @input="endDateMenu = false"></v-date-picker>
						</v-menu>
          </v-col>
          <v-col md="6" cols="12">
            <explanation-box
							id="form-end-date-explanation-box2"
							:color="'green'"
							:fullBox=true
							:displayText=endDateText>
						</explanation-box>
          </v-col>
        </v-row>
      </v-form>
      <v-card-actions>
        <v-btn text class="btn-default" @click="onOpenCancel()"> Cancel</v-btn>
        <v-spacer></v-spacer>
        <v-btn
          :loading="loadingSave"
          text
          class="btn-default"
          @click="checkForm()"
        >
          Save Changes
        </v-btn>
      </v-card-actions>
    </v-card>
		<!-- Cancel pop up -->
		<foe-dialog
      v-model="cancelDialog"		:dialogW="cancelDialog"
      persistent
			max-width="600px"
			class="cancel-dialog"
    >
      <v-card class="cancel-confirm-card">
        <v-card-title>Cancel</v-card-title>
        <v-card-text>Are you sure you want to cancel?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
					<v-btn
            text
						class="btn-default"
            @click="cancelDialog = false"
          >
            No
          </v-btn>
          <v-btn
            text
						class="btn-default"
            @click="onCancel()"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </foe-dialog>
  </div>
</template>

<script>
import { utilityService } from '@/services/utilityService.js';
export default {
	name: 'StudyDetails',
	props: {},
	data: () => ({
		version: '',
		startDate: '',
		endDate: '',
		todayDate: '',
		startDateRules: [
			v => v !== '' || 'Improper date or missing value(s)'
		],
		endDateRules: [
			v => v !== '' || 'Improper date or missing value(s)'
		],
		// recall properties
		totalRecallsRespondent: '',
		intakeTimeFrame: '',
		numLoginsAllowed: '',
		// Shared properties
		radioRules: [v => (v !== '' || 'This section is required')],
		requiredFieldRule: [v => (v && v.length > 0) || 'This section is required'],
		loadingSave: false,
		valid: true,
		saveSuccess: false,
		hasErrors: false,
		cancelDialog: false,
		studyUuid: '',
		startDateMenu: false,
		endDateMenu: false,
		startDateText: 'This is the first date that respondents may log into the respondent website to complete a recall or record.',
		endDateText: 'This is the last date that respondents may log into the respondent web site to complete a recall or record. This means that respondents will not be able to log into the respondent website after midnight (in the respondent’s time zone) on the study end date.',
		numOfRespondentsText: 'Estimate the number of unique study respondents who will complete at least one recall or record.',
		numOfRecordsText: 'A food record can be for a single-day or multiple consecutive-days. Using the examples below as reference, specify the maximum number of single-day or consecutive N-day records you want respondents to complete.<br><br><strong>Examples of 1 record:</strong><br>One single-day record<br>One consecutive 3-day record (for example: one consecutive 3-day record completed from June 1-3)<br><br><strong>Examples of 3 records:</strong><br>Three single-day records where each day is separated by at least 24 hours (For example: A single-day record is completed on June 1, July 5, and August 15)<br>Three, 3-consecutive intake day records, collecting a total of 9 intake days<br><br>Note: ASA24 does not enforce any start date requirements for records. Researchers are responsible for contacting respondents about when to complete records.',
		numOfLoginsAllowedText: '<strong>One login:</strong> Requires respondents to complete their recalls within a single login session. If respondents log out or time out, they will not be allowed to log back in to modify or complete their recall. This option may be preferable in classroom or clinic settings in which you want the recall to be completed before the respondent leaves.<br><br><strong>Multiple logins:</strong> Allows respondents to login multiple times to complete their recalls as long as the intake time frame is still open.',
		intakeTimeFrameText: '<strong>Midnight to midnight of the previous day:</strong> The respondent is prompted to enter all foods and drinks consumed during the entire previous day. For example, a respondent prompted on Sunday June 2 to complete a recall will report all foods consumed from midnight Saturday June 1 to midnight of Sunday June 2.<br><br><strong>Last 24 hours:</strong> The respondent is prompted to enter all foods and drinks consumed during the last 24 hours, starting from the time of day that the respondent logs in. For example, if the respondent logs in at 2:00 p.m. Sunday June 2 the respondent is asked to report all foods and drinks consumed from 2:00 p.m. on Saturday, June 1, to 2:00 p.m. on Sunday, June 2. Note: Respondents must complete the recall by midnight of the day they login (by midnight on Sunday, June 2, in the example above).<br><br>All times are relative to a respondent’s time zone. A respondent can login at any point to start his/her recall.',
		numOfRecallsPerRespondentsText: 'Indicate the maximum number of recalls to be reported by each respondent. This number can be changed after the study has started.<br><br>Note: Unfinished or quit recalls count toward this total number.',
		studyTypeText: '<strong>Scheduled study:</strong> You specify the intake dates for each respondent. Respondents can only login to complete recalls on the scheduled reporting dates. Intake dates can be changed or deleted up until the date occurs.<br><br><strong>Unscheduled study:</strong> Respondents can login to ASA24 at any time until the maximum number of recalls (next field) is reached. Unscheduled recalls may be preferred in research, clinical or classroom settings due to the unpredictable nature of the visits/classes or in a study in which the researcher prefers more internal controls related to scheduling recalls.<br><br><strong>Intake date</strong> is the day foods and beverages are consumed.<br><br><strong>Reporting date</strong> is the day or days that respondents login into ASA24 to report their intake. This date is derived from the intake date, intake time frame, and time to complete the recall.'
	}),
	mounted () {
		// sets the values for the displayed properties from the selected study
		this.changeSelectedFields();

		var date = new Date();
		var month = '';
		var day = '';

		if ((date.getMonth() + 1) < 10) {
			month = '0' + (date.getMonth() + 1);
		} else {
			month = date.getMonth() + 1
		}

		if (date.getDate() < 10) {
			day = '0' + date.getDate();
		} else {
			day = date.getDate();
		}

		this.todayDate = date.getFullYear() + '-' + month + '-' + day;

		// Adding these rules at the start cause errors due to todayDate variable being set to '' when the page
		// first loads
		this.endDateRules.push(v => v > this.startDate || 'Date must be greater than the start date');
		this.endDateRules.push(v => v > this.todayDate || 'Date must be greater than today');

		this.$nextTick(function () {
			// 2021.10.20 - Sebastian Calle assisted with the following idea/solution
			// Code that will run only after the entire view has been rendered
			// var b = document.getElementById('form-study-purpose');
			// b.setAttribute('aria-labelledby', 'form-study-purpose-label')
			// 2021.10.25 - Yevgeniy editted above to target class name and remove aria-labelledby attribute for all elements
			// 2022.06.29 - CHT adding to edit study
			var vRadioGroup = document.getElementsByClassName('v-input--radio-group__input');
			for (let i = 0; i < vRadioGroup.length; i++) {
				vRadioGroup[i].removeAttribute('aria-labelledby');
			}
		});
	},
	computed: {
		selectedStudy: function () {
			// If the selectedStudy changes, the radio buttons and checkbox properties need to be updated.
			if (this.studyUuid !== this.$store.getters.getSelectedStudy.studyUuid) {
				this.changeSelectedFields();
			}
			return this.$store.getters.getSelectedStudy;
		},
		currentTab: function () {
			const tabPosition = {
				tabIndex: parseInt(this.$store.getters.getCurrentStudyTabIndex),
				subTabIndex: parseInt(this.$store.getters.getCurrentStudySubTabIndex)
			};

			this.checkTabMatch(tabPosition);

			return tabPosition;
		},
		studyToSave: function () {
			// eslint-disable-next-line prefer-const
			let temp = JSON.parse(JSON.stringify(this.$store.getters.getSelectedStudy));
			return temp;
		},
		getAllStudyVersions: function () {
			return this.$store.getters.getAllStudyVersions;
		}
	},
	methods: {
		async checkTabMatch ({ tabIndex, subTabIndex } = {}) {
			var tabsPos = await utilityService.getTabsPositionByName('Edit Study Details', this.$studyTabConfig);
			if (tabsPos.tabPos === tabIndex && tabsPos.subtabPos === subTabIndex) {
				// Call focus on the dom element instead of the virtual dom. The vue focus method does
				// support the options input parameter. And we do not want to scroll the page here.
				// document.getElementById('form-total-recalls-respondent').focus({ preventScroll: true })
				setTimeout(() => {
					if (this.$refs.firstFocusableRecall) {
						this.$refs.firstFocusableRecall.focus();
					} else if (this.$refs.firstFocusableRecord) {
						this.$refs.firstFocusableRecord.focus();
					} else if (this.$refs.firstFocusableRecordSingle) {
						this.$refs.firstFocusableRecordSingle.focus();
					}
				}, '350');
			}
		},
		onOpenCancel () {
			this.cancelDialog = true;
		},
		onCancel () {
			this.$router.push('/landing');
		},
		async checkForm () {
			this.loadingSave = true;

			if (this.startDateRules.length === 1) {
				this.endDateRules.push(v => v > this.startDate || 'Date must be greater than the start date');
			}

			var valid = this.$refs.form.validate();

			if (!valid) {
				console.log('There are errors');
				this.loadingSave = false;
				this.hasErrors = true;
				this.saveSuccess = false;
				// Scroll to the top of the page to view the errors message
				window.scrollTo(0, 0);
			} else {
				const me = this;
				// make sure that time values are in unix format
				var unixStartDate = new Date(this.startDate).getTime();
				var unixEndDate = new Date(this.endDate).getTime();
				this.studyToSave.dateEnd = unixEndDate;
				this.studyToSave.dateStart = unixStartDate;

				// format fields that are different than the data field type
				this.studyToSave.midnightTimeFrame = this.intakeTimeFrame === this.$createStudyLists.INTAKE_MIDNIGHT_TO_MIDNIGHT.key ? this.$createStudyLists.INTAKE_MIDNIGHT_TO_MIDNIGHT.value : this.$createStudyLists.INTAKE_24_HOURS.value;
				this.studyToSave.multipleSessions = this.numLoginsAllowed === this.$createStudyLists.loginsAllowedList[1];
				// save form if there are no errors
				await this.$store.dispatch('updateStudy', this.studyToSave)
					.then(async function (response) {
						if (response) {
							me.saveSuccess = true;
							me.hasErrors = false;
						} else {
							me.hasErrors = true;
							me.saveSuccess = false;
						}
						me.loadingSave = false;
						window.scrollTo(0, 0);
					}).catch(function (error) {
						console.log(error);
						me.hasErrors = true;
						me.saveSuccess = false;
						me.loadingSave = false;
						window.scrollTo(0, 0);
					});
			}
		},
		changeSelectedFields () {
			this.studyUuid = this.$store.getters.getSelectedStudy.studyUuid;
			this.version = this.studyToSave.version;
			this.startDate = new Date(this.studyToSave.dateStart).toISOString().split('T')[0];
			this.endDate = new Date(this.studyToSave.dateEnd).toISOString().split('T')[0];

			if (this.studyToSave.midnightTimeFrame !== 'false') {
				this.intakeTimeFrame = this.$createStudyLists.INTAKE_MIDNIGHT_TO_MIDNIGHT.key;
			} else {
				this.intakeTimeFrame = this.$createStudyLists.INTAKE_24_HOURS.key;
			}

			if (this.studyToSave.multipleSessions !== 'false') {
				this.numLoginsAllowed = this.$createStudyLists.loginsAllowedList[1];
			} else {
				this.numLoginsAllowed = this.$createStudyLists.loginsAllowedList[0];
			}
		}
	}
};
</script>

<style>
.no-click {
    pointer-events: none;
}
</style>
